import React from "react";
import {
  Skeleton,
  SkeletonCardGrid,
  SkeletonChat,
  SkeletonDetail,
  SkeletonForm,
  SkeletonList,
  SkeletonPageHeader,
  SkeletonPanel,
  SkeletonRegion,
  SkeletonStatGrid,
  SkeletonTable,
  SkeletonTabs,
  SkeletonText,
  SkeletonToggleList,
} from "./Skeleton";

// Page-shaped skeletons. Each mirrors the real layout of the module it stands in for, so content
// replaces it without the page jumping.

const repeat = (count, render) => Array.from({ length: count }, (_, index) => render(index));

/* ---------- Customer workspace ---------- */

export const BusinessProfileSkeleton = () => (
  <SkeletonRegion label="Loading your business profile" className="sk-page">
    <SkeletonPageHeader actions={1} />
    <div className="sk-card sk-detail-summary">
      <Skeleton width={72} height={72} radius={18} />
      <div className="sk-grow"><Skeleton width="min(240px, 60%)" height={20} /><Skeleton width="min(320px, 80%)" height={11} /><Skeleton width={160} height={10} /></div>
    </div>
    <div className="sk-two-col">
      {repeat(4, (index) => <SkeletonPanel key={index}><SkeletonText lines={index % 2 ? 4 : 3} /></SkeletonPanel>)}
    </div>
  </SkeletonRegion>
);

export const AccountProfileSkeleton = () => (
  <SkeletonRegion label="Loading your account profile" className="sk-page">
    <SkeletonPageHeader actions={0} />
    <div className="sk-card"><SkeletonForm fields={6} /></div>
    <div className="sk-card"><SkeletonForm fields={3} columns={3} /></div>
  </SkeletonRegion>
);

export const SettingsSectionSkeleton = () => (
  <SkeletonRegion label="Loading your settings">
    <div className="sk-card"><SkeletonForm fields={4} title={false} actions={false} /></div>
    <div className="sk-card"><SkeletonToggleList rows={4} /></div>
  </SkeletonRegion>
);

export const UpgradePlanSkeleton = () => (
  <SkeletonRegion label="Loading plans and subscription details" className="sk-page">
    <SkeletonPageHeader actions={0} />
    <div className="sk-card sk-detail-summary">
      <Skeleton width={46} height={46} radius={13} />
      <div className="sk-grow"><Skeleton width="min(200px, 50%)" height={16} /><Skeleton width="min(360px, 85%)" height={10} /></div>
      <Skeleton width={120} height={36} radius={10} />
    </div>
    <SkeletonTabs count={2} />
    <div className="sk-card-grid sk-plan-grid">
      {repeat(3, (index) => (
        <div className="sk-card sk-plan" key={index}>
          <Skeleton width="45%" height={14} />
          <Skeleton width="60%" height={30} radius={8} />
          <SkeletonText lines={2} lastWidth="75%" />
          <div className="sk-plan-features">{repeat(5, (row) => <div className="sk-card-row" key={row}><Skeleton width={16} height={16} circle /><Skeleton width={`${60 + (row % 3) * 12}%`} height={10} /></div>)}</div>
          <Skeleton height={40} radius={10} />
        </div>
      ))}
    </div>
  </SkeletonRegion>
);

export const SupportCategorySkeleton = () => (
  <SkeletonRegion label="Loading help topics" className="sk-support-categories">
    <SkeletonCardGrid count={6} min={220} lines={0} />
  </SkeletonRegion>
);

export const SupportFaqSkeleton = () => (
  <SkeletonRegion label="Loading help articles">
    {repeat(2, (group) => (
      <div className="sk-faq-group" key={group}>
        <Skeleton width={140} height={12} />
        {repeat(3, (row) => <div className="sk-faq-row" key={row}><Skeleton width={`${70 - row * 10}%`} height={12} /><Skeleton width={18} height={18} radius={5} /></div>)}
      </div>
    ))}
  </SkeletonRegion>
);

export const TicketListSkeleton = ({ rows = 4, label = "Loading your tickets" }) => (
  <SkeletonRegion label={label}>
    <div className="sk-ticket-list">
      {repeat(rows, (index) => (
        <div className="sk-ticket" key={index}>
          <div className="sk-card-row"><Skeleton width={84} height={11} /><span className="sk-grow" /><Skeleton width={70} height={20} radius={999} /></div>
          <Skeleton width={`${80 - (index % 3) * 15}%`} height={12} />
          <Skeleton width="45%" height={10} />
        </div>
      ))}
    </div>
  </SkeletonRegion>
);

export const ConversationSkeleton = ({ label = "Loading conversation" }) => (
  <SkeletonRegion label={label}>
    <div className="sk-card-row"><div className="sk-grow"><Skeleton width="55%" height={16} /><Skeleton width="35%" height={10} /></div><Skeleton width={80} height={24} radius={999} /></div>
    <SkeletonChat messages={4} />
  </SkeletonRegion>
);

export const LeadsSkeleton = () => (
  <SkeletonRegion label="Loading leads">
    <SkeletonStatGrid count={4} min={180} />
    <div className="sk-card"><div className="sk-pipeline">{repeat(5, (index) => <div className="sk-grow" key={index}><Skeleton width="60%" height={10} /><Skeleton height={8} radius={999} /></div>)}</div></div>
    <div className="sk-two-col">
      <SkeletonPanel><div className="sk-bars">{repeat(14, (index) => <Skeleton key={index} height={`${30 + ((index * 37) % 60)}%`} radius={4} />)}</div></SkeletonPanel>
      <SkeletonPanel><SkeletonList rows={4} /></SkeletonPanel>
    </div>
  </SkeletonRegion>
);

export const AutomationRulesSkeleton = () => (
  <SkeletonRegion label="Loading automation rules">
    <SkeletonTable rows={5} columns={5} avatar={false} />
  </SkeletonRegion>
);

export const PreferencesSkeleton = ({ label = "Loading preferences" }) => (
  <SkeletonRegion label={label}>
    <SkeletonToggleList rows={5} />
  </SkeletonRegion>
);

export const PickerListSkeleton = ({ label = "Loading", rows = 4 }) => (
  <SkeletonRegion label={label}>
    <SkeletonList rows={rows} meta={false} />
  </SkeletonRegion>
);

/* ---------- Admin portal ---------- */

export const AdminTableSkeleton = ({ label, rows = 8, columns = 7 }) => (
  <SkeletonRegion label={label}>
    <SkeletonTable rows={rows} columns={columns} />
  </SkeletonRegion>
);

export const AdminDetailSkeleton = ({ label, tabs = 3 }) => (
  <SkeletonRegion label={label}>
    <SkeletonDetail tabs={tabs} />
  </SkeletonRegion>
);

export const IntegrationCardsSkeleton = () => (
  <SkeletonRegion label="Loading integration settings">
    <div className="sk-card"><div className="sk-card-row"><div className="sk-grow"><Skeleton width="38%" height={16} /><Skeleton width="70%" height={10} /></div><Skeleton width={130} height={36} radius={10} /></div></div>
    <SkeletonCardGrid count={6} min={300} lines={2} footer />
  </SkeletonRegion>
);

export const BillingTabSkeleton = () => (
  <SkeletonRegion label="Loading billing configuration">
    <SkeletonPanel><SkeletonTable rows={5} columns={6} avatar={false} /></SkeletonPanel>
  </SkeletonRegion>
);

export const ProviderSettingsSkeleton = ({ label, banner = false }) => (
  <SkeletonRegion label={label} className="sk-page">
    <SkeletonPageHeader actions={1} />
    {banner && <div className="sk-card sk-detail-summary"><Skeleton width={46} height={46} radius={13} /><div className="sk-grow"><Skeleton width="30%" height={10} /><Skeleton width="40%" height={18} /><Skeleton width="65%" height={10} /></div><Skeleton width={180} height={44} radius={10} /></div>}
    <SkeletonCardGrid count={4} min={280} lines={3} footer />
    <div className="sk-card"><SkeletonForm fields={4} /></div>
  </SkeletonRegion>
);

export const AccessPanelSkeleton = () => (
  <SkeletonRegion label="Loading roles and permissions">
    <SkeletonPanel><SkeletonTable rows={6} columns={5} /></SkeletonPanel>
  </SkeletonRegion>
);
